import { useCallback, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import ChevronDownIcon from '~/assets/svgs/chevron-down.svg';
import Option from './Option';
import { SelectProvider } from './select-context';
import useClickAway from '~/hooks/useClickAway';

const Select = ({
  className,
  children,
  label,
  selectedLabel,
  value,
  onChange,
}) => {
  const selectRef = useRef();
  const [isOpen, setOpen] = useState(false);
  const [selected, setSelected] = useState(value);

  useClickAway(selectRef, () => setOpen(false));

  const onOptionSelect = useCallback((option) => {
    setSelected(option);
    setOpen(false);
  }, []);

  useEffect(() => {
    if (value !== selected) {
      setSelected(value);
    }
  }, [value]);

  useEffect(() => {
    onChange?.(selected);
  }, [selected]);

  return (
    <SelectProvider onOptionSelect={onOptionSelect}>
      <S.Container className={className} ref={selectRef}>
        <S.Label>{label}</S.Label>
        <S.Select>
          <S.Button onClick={() => setOpen((p) => !p)}>
            {selectedLabel || 'Select'}
            <S.ChevronIcon $isOpen={isOpen} />
          </S.Button>
          {isOpen && <S.Content>{children}</S.Content>}
        </S.Select>
      </S.Container>
    </SelectProvider>
  );
};

Select.Option = Option;

const S = {
  Container: styled.div`
    display: block;
    width: 100%;
  `,
  Select: styled.div`
    position: relative;
  `,
  Button: styled.button`
    display: block;
    width: 100%;
    border: 0.6px solid #d5d5d5;
    background: #f5f6fa;
    color: #838383;
    padding: 1.25rem 2.5rem 1.25rem 1.25rem;
    border-radius: 4px;
    font-size: 1rem;
    line-height: 1.35;
    font-weight: 200;
    letter-spacing: 1px;
    margin: 0 0.5rem 0.5rem 0;
    cursor: pointer;
    text-align: left;
    position: relative;

    &:hover,
    &:focus {
      outline-color: ${({ theme }) => theme.colors.primary};
    }
  `,
  Content: styled.div`
    background: #fff;
    border-radius: 4px;
    border: 0.6px solid #d5d5d5;
    padding: 0.5rem 0;
    position: absolute;
    width: 100%;
    z-index: 1;
  `,
  Label: styled.label`
    display: block;
    width: 100%;
    padding: 0 1rem 1rem 0;
    font-size: 1.2rem;
    letter-spacing: 1px;
    opacity: 0.6;
  `,
  ChevronIcon: styled(ChevronDownIcon)`
    width: 1rem;
    height: 1rem;
    position: absolute;
    right: 0;
    top: 50%;
    transform: ${({ $isOpen }) =>
      $isOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)'};
    transition: transform 200ms;
    right: 1rem;
  `,
};

export default Select;
